'use client';

import { Search, X } from 'lucide-react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

type BulkCurrencyPickerProps = {
  currencies: string[];
  selected: string[];
  onChange: (selected: string[]) => void;
  disabled?: boolean;
  searchPlaceholder?: string;
};

const filterCurrencies = (currencies: string[], query: string) => {
  const normalized = query.trim().toLowerCase();
  if (!normalized) return currencies;
  return currencies.filter((currency) => currency.toLowerCase().includes(normalized));
};

const toggleCurrency = (selected: string[], currency: string) =>
  selected.includes(currency)
    ? selected.filter((item) => item !== currency)
    : [...selected, currency];

const removeCurrency = (selected: string[], currency: string) =>
  selected.filter((item) => item !== currency);

const MAX_VISIBLE_BADGES = 5;

const getVisibleCurrencies = (selected: string[]) => selected.slice(0, MAX_VISIBLE_BADGES);

const getHiddenCurrencies = (selected: string[]) => selected.slice(MAX_VISIBLE_BADGES);

type CurrencyBadgeProps = {
  currency: string;
  disabled?: boolean;
  onRemove: (currency: string) => void;
};

const CurrencyBadge = ({ currency, disabled, onRemove }: CurrencyBadgeProps) => (
  <Badge variant="secondary" className="gap-1 pr-1 text-xs">
    {currency}
    <button
      type="button"
      disabled={disabled}
      onClick={() => onRemove(currency)}
      className="hover:bg-muted rounded-sm p-0.5 disabled:pointer-events-none"
      aria-label={`Remove ${currency}`}
    >
      <X className="size-3" />
    </button>
  </Badge>
);

type HiddenCurrenciesBadgeProps = {
  hiddenCurrencies: string[];
  label: string;
};

const HiddenCurrenciesBadge = ({ hiddenCurrencies, label }: HiddenCurrenciesBadgeProps) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <Badge variant="secondary" className="cursor-default text-xs">
        {label}
      </Badge>
    </TooltipTrigger>
    <TooltipContent side="top" className="max-w-48">
      <div className="flex flex-col gap-0.5 text-left">
        {hiddenCurrencies.map((currency) => (
          <span key={currency}>{currency}</span>
        ))}
      </div>
    </TooltipContent>
  </Tooltip>
);

type SelectedCurrencyBadgesProps = {
  selected: string[];
  disabled?: boolean;
  othersLabel: (count: number) => string;
  onRemove: (currency: string) => void;
};

const SelectedCurrencyBadges = ({
  selected,
  disabled,
  othersLabel,
  onRemove,
}: SelectedCurrencyBadgesProps) => {
  if (selected.length === 0) return null;
  const visibleCurrencies = getVisibleCurrencies(selected);
  const hiddenCurrencies = getHiddenCurrencies(selected);
  return (
    <div className="flex flex-wrap items-center gap-1.5 border-b px-3 py-2">
      {visibleCurrencies.map((currency) => (
        <CurrencyBadge key={currency} currency={currency} disabled={disabled} onRemove={onRemove} />
      ))}
      {hiddenCurrencies.length > 0 ? (
        <HiddenCurrenciesBadge
          hiddenCurrencies={hiddenCurrencies}
          label={othersLabel(hiddenCurrencies.length)}
        />
      ) : null}
    </div>
  );
};

type CurrencyListProps = {
  currencies: string[];
  selected: string[];
  disabled?: boolean;
  emptyMessage: string;
  onToggle: (currency: string) => void;
};

const CurrencyList = ({ currencies, selected, disabled, emptyMessage, onToggle }: CurrencyListProps) => (
  <div
    className="max-h-52 overflow-y-auto overscroll-contain p-1"
    onWheel={(event) => event.stopPropagation()}
  >
    {currencies.length === 0 ? (
      <p className="text-muted-foreground px-3 py-6 text-center text-sm">{emptyMessage}</p>
    ) : (
      currencies.map((currency) => {
        const isChecked = selected.includes(currency);
        return (
          <label
            key={currency}
            className={cn(
              'hover:bg-accent flex cursor-pointer items-center gap-3 rounded-sm px-3 py-2 text-sm',
              disabled && 'pointer-events-none opacity-50',
            )}
          >
            <Checkbox
              checked={isChecked}
              disabled={disabled}
              onCheckedChange={() => onToggle(currency)}
            />
            <span className="font-medium">{currency}</span>
          </label>
        );
      })
    )}
  </div>
);

export const BulkCurrencyPicker = ({
  currencies,
  selected,
  onChange,
  disabled = false,
  searchPlaceholder,
}: BulkCurrencyPickerProps) => {
  const { t } = useTranslation('common');
  const [query, setQuery] = React.useState('');
  const filteredCurrencies = React.useMemo(
    () => filterCurrencies(currencies, query),
    [currencies, query],
  );

  const handleToggle = (currency: string) => {
    onChange(toggleCurrency(selected, currency));
  };

  const handleRemove = (currency: string) => {
    onChange(removeCurrency(selected, currency));
  };

  return (
    <div className="bg-card overflow-hidden rounded-md border">
      <div className="relative border-b px-3 py-2">
        <Search className="text-muted-foreground pointer-events-none absolute left-5 top-1/2 size-4 -translate-y-1/2" />
        <Input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={searchPlaceholder ?? t('currencySelect.searchPlaceholder')}
          disabled={disabled}
          className="h-9 border-0 bg-transparent pl-9 shadow-none focus-visible:ring-0"
        />
      </div>
      <SelectedCurrencyBadges
        selected={selected}
        disabled={disabled}
        othersLabel={(count) => t('currencySelect.andOthers', { count })}
        onRemove={handleRemove}
      />
      <CurrencyList
        currencies={filteredCurrencies}
        selected={selected}
        disabled={disabled}
        emptyMessage={t('currencySelect.noCurrenciesFound')}
        onToggle={handleToggle}
      />
    </div>
  );
};
